/** 重ねて出す画面：予報、予定外の着陸、Day のリザルト、最終リザルト（docs/06_ui-ux.md 5 章） */
import { formatDuration } from '../../core/units';
import type { Forecast } from '../../sim/weather/forecast';
import { el } from '../layout';
import { compass8, windArrow } from '../panels/weatherTimeline';

export interface ResultRow {
  rank: number;
  name: string;
  color: string;
  isPlayer: boolean;
  remainingKm: number;
  gapS: number | null;
  finished: boolean;
  todayKm: number;
  flights: number;
  longestKm: number;
  stamina: number;
}

export interface Dialogs {
  showForecast(f: Forecast, onClose: () => void): void;
  showLanding(
    info: { place: string; packMin: number; stamina: number; resumeHint: string },
    onPlan: () => void,
    onResume: () => void,
  ): void;
  showResults(title: string, rows: ResultRow[], buttonLabel: string, onNext: () => void): void;
  close(): void;
  readonly open: boolean;
}

export function createDialogs(host: HTMLElement): Dialogs {
  const backdrop = el('div', 'dialog-backdrop');
  backdrop.hidden = true;
  host.appendChild(backdrop);
  let isOpen = false;

  const show = (card: HTMLElement): void => {
    backdrop.replaceChildren(card);
    backdrop.hidden = false;
    isOpen = true;
  };
  const close = (): void => {
    backdrop.hidden = true;
    backdrop.replaceChildren();
    isOpen = false;
  };
  const button = (label: string, cls: string, onClick: () => void): HTMLButtonElement => {
    const b = el('button', `btn ${cls}`, label);
    b.addEventListener('click', onClick);
    return b;
  };

  return {
    get open() {
      return isOpen;
    },
    close,
    showForecast(f, onClose) {
      const card = el('div', 'dialog dialog--forecast');
      card.append(el('h2', 'dialog__title', `Day${f.dayIndex + 1} の予報：${f.profileName}`));
      const table = el('table', 'forecast-table');
      const head = el('tr');
      head.append(el('th', '', '時間帯'), el('th', '', '谷'), el('th', '', '稜線'), el('th', '', '山頂'));
      table.appendChild(head);
      for (const b of f.windBlocks) {
        const tr = el('tr');
        tr.appendChild(el('td', '', `${b.fromH}〜${b.toH}時`));
        for (const w of [b.valley, b.ridge, b.peak])
          tr.appendChild(el('td', '', `${windArrow(w.fromDeg)} ${compass8(w.fromDeg)} ${w.kmh}km/h`));
        table.appendChild(tr);
      }
      const t = f.thermal;
      const cb = f.cloudBase.map((c) => `${c.h}時 ${c.m.toLocaleString()}m`).join('、');
      const storm =
        f.storm.chance > 0
          ? `${Math.round(f.storm.chance * 100)}%（${Math.floor(f.storm.fromH)}〜${Math.ceil(f.storm.toH)}時、${f.storm.area?.label ?? '場所未定'}）`
          : 'なし';
      const facts = el('dl', 'forecast-facts');
      const fact = (k: string, v: string): void => {
        facts.append(el('dt', '', k), el('dd', '', v));
      };
      fact(
        'サーマル',
        `${t.label}（最大 ${t.maxClimbMs.toFixed(1)}m/s、${Math.floor(t.startH)}時ごろ〜${Math.ceil(t.endH)}時ごろ、最盛期 ${Math.round(t.peakH)}時ごろ）`,
      );
      fact('雲底', cb);
      fact('雷雨', storm);
      fact('ヒント', f.hint);
      card.append(
        el('h3', 'dialog__sub', '風'),
        table,
        facts,
        el('p', 'dialog__note', '予報は外れることがあります。雲の育ち方や木の揺れで答え合わせをしましょう。'),
      );
      card.appendChild(
        button('閉じる', 'btn--primary', () => {
          close();
          onClose();
        }),
      );
      show(card);
    },
    showLanding(info, onPlan, onResume) {
      const card = el('div', 'dialog');
      card.append(
        el('h2', 'dialog__title', '予定外の着陸'),
        el(
          'p',
          '',
          `${info.place}に降りました。格納に約 ${info.packMin} 分かかります。スタミナ ${Math.round(info.stamina)}。`,
        ),
        el('p', 'dialog__note', 'ここからのルートを組み直してください。'),
        el('p', 'dialog__note', info.resumeHint),
      );
      const row = el('div', 'dialog__buttons');
      row.append(
        button('ルートを組む', 'btn--primary', () => {
          close();
          onPlan();
        }),
        button('そのまま再開', '', () => {
          close();
          onResume();
        }),
      );
      card.appendChild(row);
      show(card);
    },
    showResults(title, rows, buttonLabel, onNext) {
      const card = el('div', 'dialog dialog--results');
      card.appendChild(el('h2', 'dialog__title', title));
      const table = el('table', 'results-table');
      const head = el('tr');
      for (const h of [
        '順位',
        '選手',
        'ゴールまで',
        'トップとの差',
        '今日の距離',
        'フライト',
        '最長',
        'スタミナ',
      ])
        head.appendChild(el('th', '', h));
      table.appendChild(head);
      for (const r of rows) {
        const tr = el('tr', r.isPlayer ? 'results-table__player' : '');
        const name = el('td', 'results-table__name', r.name);
        name.style.setProperty('--c', r.color);
        tr.append(
          el('td', '', String(r.rank)),
          name,
          el('td', '', r.finished ? 'ゴール' : `${r.remainingKm.toFixed(1)}km`),
          el('td', '', r.rank === 1 ? '—' : r.gapS === null ? '?' : `+${formatDuration(r.gapS)}`),
          el('td', '', `${r.todayKm.toFixed(1)}km`),
          el('td', '', `${r.flights} 回`),
          el('td', '', r.longestKm > 0 ? `${r.longestKm.toFixed(1)}km` : '—'),
          el('td', '', String(Math.round(r.stamina))),
        );
        table.appendChild(tr);
      }
      card.append(
        table,
        button(buttonLabel, 'btn--primary', () => {
          close();
          onNext();
        }),
      );
      show(card);
    },
  };
}
